import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AIClassificationJobRecord,
  AIClassificationResponse,
  AIClassificationsResponse,
  FollowUpAnswer
} from '../../types/AIClassification.types';
import { ChangeAssessmentToAIResponse } from '../../types/AISystems.types';
import type { RootState } from '../index';

interface AIPoweredState {
  aiSystemId: string | null;
  jobId: string | null;
  currentJob: AIClassificationJobRecord | null;
  classifications: AIClassificationJobRecord[];
  followUpAnswers: FollowUpAnswer[];
  currentAnswer: string;
  loading: boolean;
  polling: boolean;
  submittingAnswer: boolean;
  rerunLoading: boolean;
  rerunClassificationType: 'description' | 'answers' | null;
  resumeLoading: boolean;
  signLoading: boolean;
  error: string | null;
  errorStatus: number | null;
  editedAtIndex: number | null;
  editedDescription: string;
  isEditingQuestionnaireItem: boolean;
  editingAnswerIndex: number | null;
  showEditAnswerClarification: boolean;
  isInitialized: boolean;
  isEditClassificationPathEnabled: boolean;
}

type AIPoweredErrorPayload =
  | string
  | {
      message: string;
      status?: number | null;
    };

const getAIPoweredErrorPayload = (payload: AIPoweredErrorPayload) => {
  if (typeof payload === 'string') {
    return {
      message: payload,
      status: null
    };
  }

  return {
    message: payload.message,
    status: payload.status ?? null
  };
};

const initialState: AIPoweredState = {
  aiSystemId: null,
  jobId: null,
  currentJob: null,
  classifications: [],
  followUpAnswers: [],
  currentAnswer: '',
  loading: false,
  polling: false,
  submittingAnswer: false,
  rerunLoading: false,
  rerunClassificationType: null,
  resumeLoading: false,
  signLoading: false,
  error: null,
  errorStatus: null,
  editedAtIndex: null,
  editedDescription: '',
  isEditingQuestionnaireItem: false,
  editingAnswerIndex: null,
  showEditAnswerClarification: false,
  isInitialized: false,
  isEditClassificationPathEnabled: false
};

export const selectEffectiveFollowUpAnswers = (state: RootState) =>
  state.aiPowered.currentJob?.followUpAnswers ??
  state.aiPowered.followUpAnswers;

export const isAIClassificationProcessing = (state: RootState) => {
  const aiPowered = state.aiPowered;

  return (
    aiPowered.loading ||
    aiPowered.polling ||
    aiPowered.currentJob?.status === 'PENDING' ||
    aiPowered.currentJob?.status === 'PROCESSING' ||
    aiPowered.currentJob?.processState === 'IN_PROGRESS'
  );
};

export const isAnalyzingDescription = (state: RootState) => {
  const aiPowered = state.aiPowered;
  const effectiveFollowUpAnswers = selectEffectiveFollowUpAnswers(state);
  const isDescriptionRerunActive =
    aiPowered.rerunClassificationType === 'description' &&
    (aiPowered.rerunLoading ||
      !aiPowered.currentJob ||
      isAIClassificationProcessing(state));

  return (
    isDescriptionRerunActive ||
    (isAIClassificationProcessing(state) &&
      effectiveFollowUpAnswers.length === 0)
  );
};

export const isLoadingNextQuestion = (state: RootState) => {
  const effectiveFollowUpAnswers = selectEffectiveFollowUpAnswers(state);

  return (
    isAIClassificationProcessing(state) && effectiveFollowUpAnswers.length > 0
  );
};

export const isAwaitingAIAnswer = (state: RootState) =>
  Boolean(
    state.aiPowered.currentJob?.followUpQuestion &&
    state.aiPowered.currentJob.processState === 'AWAITING_USER_INPUT' &&
    state.stepper.startAiSystemBodyRequest.assessmentType === 'AI_POWERED'
  );

export const isEditingOptionAnswer = (state: RootState) => {
  const editingAnswerIndex = state.aiPowered.editingAnswerIndex;
  const editedAnswer =
    editingAnswerIndex !== null
      ? state.aiPowered.followUpAnswers[editingAnswerIndex]
      : null;
  const editedQuestionHasMatchingOption = Boolean(
    editedAnswer?.options?.length &&
      editedAnswer.options.some(
        (option) => option.trim() === editedAnswer.answer.trim()
      )
  );

  return (
    state.aiPowered.isEditingQuestionnaireItem &&
    state.aiPowered.showEditAnswerClarification &&
    editingAnswerIndex !== null &&
    editedQuestionHasMatchingOption
  );
};

export const shouldShowClarification = (state: RootState) => {
  if (state.aiPowered.isEditingQuestionnaireItem) {
    return isEditingOptionAnswer(state);
  }

  return isAwaitingAIAnswer(state);
};

export const aiPoweredSlice = createSlice({
  name: 'aiPowered',
  initialState,
  reducers: {
    startClassificationStart: (state, action: PayloadAction<string>) => {
      state.aiSystemId = action.payload;
      state.loading = true;
      state.error = null;
      state.errorStatus = null;
    },
    startClassificationSuccess: (
      state,
      action: PayloadAction<AIClassificationResponse>
    ) => {
      state.loading = false;
      state.jobId = action.payload.jobId;
      state.currentJob = null;
      state.isInitialized = true;
      state.error = null;
      state.errorStatus = null;
    },
    startClassificationFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.loading = false;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    switchToAIClassificationSuccess: (
      state,
      action: PayloadAction<ChangeAssessmentToAIResponse>
    ) => {
      state.aiSystemId = action.payload.aiSystemId;
      state.jobId = action.payload.jobId;
      state.currentJob = null;
      state.followUpAnswers = [];
      state.currentAnswer = '';
      state.loading = false;
      state.polling = false;
      state.error = null;
      state.errorStatus = null;
      state.isInitialized = true;
    },
    fetchClassificationsStart: (state) => {
      state.loading = true;
      state.error = null;
      state.errorStatus = null;
    },
    fetchClassificationsSuccess: (
      state,
      action: PayloadAction<AIClassificationsResponse>
    ) => {
      state.loading = false;
      state.classifications = action.payload;
      state.currentJob = action.payload[0] ?? null;
      state.aiSystemId = action.payload[0]?.aiSystemId ?? state.aiSystemId;
      state.jobId = action.payload[0]?.jobId ?? state.jobId;
      if (
        action.payload[0]?.followUpAnswers &&
        action.payload[0].followUpAnswers.length >= state.followUpAnswers.length
      ) {
        state.followUpAnswers = action.payload[0].followUpAnswers;
      }
      state.isInitialized = Boolean(action.payload[0]) || state.isInitialized;
      state.error = null;
      state.errorStatus = null;
    },
    fetchClassificationsFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.loading = false;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    pollClassificationStart: (state) => {
      state.polling = true;
      state.error = null;
      state.errorStatus = null;
    },
    pollClassificationSuccess: (
      state,
      action: PayloadAction<AIClassificationJobRecord>
    ) => {
      state.polling = false;
      state.aiSystemId = action.payload.aiSystemId;
      state.currentJob = action.payload;
      state.jobId = action.payload.jobId;
      if (
        action.payload.followUpAnswers &&
        action.payload.followUpAnswers.length >= state.followUpAnswers.length
      ) {
        state.followUpAnswers = action.payload.followUpAnswers;
      }
      if (
        action.payload.status !== 'PENDING' &&
        action.payload.status !== 'PROCESSING' &&
        action.payload.processState !== 'IN_PROGRESS'
      ) {
        state.rerunClassificationType = null;
      }
      state.isInitialized = true;
      state.error = null;
      state.errorStatus = null;
    },
    pollClassificationFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.polling = false;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    submitFollowUpAnswerStart: (state) => {
      state.submittingAnswer = true;
      state.rerunClassificationType = null;
      state.error = null;
      state.errorStatus = null;
    },
    submitFollowUpAnswerSuccess: (
      state,
      action: PayloadAction<AIClassificationResponse>
    ) => {
      state.submittingAnswer = false;
      state.jobId = action.payload.jobId;
      state.currentJob = null;
      state.currentAnswer = '';
      state.error = null;
      state.errorStatus = null;
    },
    submitFollowUpAnswerFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.submittingAnswer = false;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    rerunClassificationStart: (
      state,
      action: PayloadAction<'description' | 'answers'>
    ) => {
      state.rerunLoading = true;
      state.rerunClassificationType = action.payload;
      state.error = null;
      state.errorStatus = null;
    },
    rerunClassificationSuccess: (
      state,
      action: PayloadAction<AIClassificationResponse>
    ) => {
      state.rerunLoading = false;
      state.jobId = action.payload.jobId;
      state.currentJob = null;
      state.followUpAnswers = [];
      state.currentAnswer = '';
      state.editedAtIndex = null;
      state.editedDescription = '';
      state.isInitialized = true;
      state.error = null;
      state.errorStatus = null;
    },
    rerunClassificationFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.rerunLoading = false;
      state.rerunClassificationType = null;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    resumeClassificationStart: (state) => {
      state.resumeLoading = true;
      state.error = null;
      state.errorStatus = null;
    },
    resumeClassificationSuccess: (
      state,
      action: PayloadAction<AIClassificationJobRecord>
    ) => {
      state.resumeLoading = false;
      state.aiSystemId = action.payload.aiSystemId;
      state.currentJob = action.payload;
      state.jobId = action.payload.jobId;
      state.error = null;
      state.errorStatus = null;
    },
    resumeClassificationFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.resumeLoading = false;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    signClassificationStart: (state) => {
      state.signLoading = true;
      state.error = null;
      state.errorStatus = null;
    },
    signClassificationSuccess: (
      state,
      action: PayloadAction<AIClassificationJobRecord>
    ) => {
      state.signLoading = false;
      state.aiSystemId = action.payload.aiSystemId;
      state.currentJob = action.payload;
      state.jobId = action.payload.jobId;
      state.error = null;
      state.errorStatus = null;
    },
    signClassificationFailure: (
      state,
      action: PayloadAction<AIPoweredErrorPayload>
    ) => {
      const error = getAIPoweredErrorPayload(action.payload);
      state.signLoading = false;
      state.error = error.message;
      state.errorStatus = error.status;
    },
    setCurrentAnswer: (state, action: PayloadAction<string>) => {
      state.currentAnswer = action.payload;
    },
    setFollowUpAnswers: (state, action: PayloadAction<FollowUpAnswer[]>) => {
      state.followUpAnswers = action.payload;
    },
    addFollowUpAnswer: (state, action: PayloadAction<FollowUpAnswer>) => {
      state.followUpAnswers.push(action.payload);
    },
    updateFollowUpAnswer: (
      state,
      action: PayloadAction<{ index: number; answer: FollowUpAnswer }>
    ) => {
      state.followUpAnswers[action.payload.index] = action.payload.answer;
    },
    removeFollowUpAnswersAfter: (state, action: PayloadAction<number>) => {
      state.followUpAnswers = state.followUpAnswers.slice(
        0,
        action.payload + 1
      );
    },
    setEditedAtIndex: (state, action: PayloadAction<number | null>) => {
      state.editedAtIndex = action.payload;
    },
    setEditedDescription: (state, action: PayloadAction<string>) => {
      state.editedDescription = action.payload;
    },
    setIsEditingQuestionnaireItem: (state, action: PayloadAction<boolean>) => {
      state.isEditingQuestionnaireItem = action.payload;
    },
    setEditAnswerClarificationContext: (
      state,
      action: PayloadAction<{ index: number | null; show: boolean }>
    ) => {
      state.editingAnswerIndex = action.payload.index;
      state.showEditAnswerClarification = action.payload.show;
    },
    setIsEditClassificationPathEnabled: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isEditClassificationPathEnabled = action.payload;
    },
    clearAIPoweredError: (state) => {
      state.error = null;
      state.errorStatus = null;
    },
    resetAIPowered: () => initialState
  }
});

export const {
  startClassificationStart,
  startClassificationSuccess,
  startClassificationFailure,
  switchToAIClassificationSuccess,
  fetchClassificationsStart,
  fetchClassificationsSuccess,
  fetchClassificationsFailure,
  pollClassificationStart,
  pollClassificationSuccess,
  pollClassificationFailure,
  submitFollowUpAnswerStart,
  submitFollowUpAnswerSuccess,
  submitFollowUpAnswerFailure,
  rerunClassificationStart,
  rerunClassificationSuccess,
  rerunClassificationFailure,
  resumeClassificationStart,
  resumeClassificationSuccess,
  resumeClassificationFailure,
  signClassificationStart,
  signClassificationSuccess,
  signClassificationFailure,
  setCurrentAnswer,
  setFollowUpAnswers,
  addFollowUpAnswer,
  updateFollowUpAnswer,
  removeFollowUpAnswersAfter,
  setEditedAtIndex,
  setEditedDescription,
  setIsEditingQuestionnaireItem,
  setEditAnswerClarificationContext,
  setIsEditClassificationPathEnabled,
  clearAIPoweredError,
  resetAIPowered
} = aiPoweredSlice.actions;

export default aiPoweredSlice.reducer;
